import type { CodeHistory, CodeChangeEvent, CodePaste } from '@yandex-int/k-common/typings';
import type { Text } from '@codemirror/state';

interface IndexedPaste {
    /**
     * Determines paste moment in relaion to the code history.
     *
     * The index is equal to the undo stack length at the moment
     * of the paste operation.
     */
    readonly operationIndex: number;
    /**
     * Data for the captured paste operation.
     */
    readonly paste: CodePaste;
}

interface HistorySize {
    readonly undo: number;
    readonly redo: number;
}

/**
 * Immutable tracked paste state to make it very cheap to capture
 * and keep a copy of it.
 */
interface CodePasteState {
    readonly undoStack: readonly IndexedPaste[];
    readonly redoStack: readonly IndexedPaste[];
    readonly historyAt: HistorySize;
}

/**
 * Tracks pastes to a text editor to keep them alongside
 * text change events (edit history).
 */
export class CodePasteTracker {
    private state: CodePasteState = {
        undoStack: [],
        redoStack: [],
        historyAt: { undo: 0, redo: 0 },
    };

    attachPaste(paste: CodePaste, at: HistorySize): void {
        const undoStack = [...this.state.undoStack];
        undoStack.push({ operationIndex: at.undo, paste });
        this.state = { ...this.state, undoStack };
    }

    /**
     * Handler which must be called on every code history change
     * (including a normal code changes or performing an undo or a redo)
     * to maintain a side-by-side paste operation metadata.
     */
    onChange(at: HistorySize): void {
        if (at.undo === this.state.historyAt.undo && at.redo === this.state.historyAt.redo) {
            // History has not changed, keep the same state
            return;
        }

        const previousSize = this.state.historyAt.undo + this.state.historyAt.redo;
        const changedSize = at.undo + at.redo;

        const undoStack = [...this.state.undoStack];
        const redoStack = [...this.state.redoStack];

        if (changedSize === previousSize) {
            const operationIndex = at.undo;

            // Move each undone paste to the redo stack
            while (undoStack.length > 0 && undoStack.at(-1)!.operationIndex >= operationIndex) {
                const paste = undoStack.pop()!;
                redoStack.push(paste);
            }

            // Move each redone paste to the undo stack
            while (redoStack.length > 0 && redoStack.at(-1)!.operationIndex < operationIndex) {
                const paste = redoStack.pop()!;
                undoStack.push(paste);
            }
        } else {
            // Total history size changed (e.g. typed some new text),
            // which means all redo operations are gone
            redoStack.length = 0;
        }

        this.state = {
            undoStack,
            redoStack,
            historyAt: at,
        };
    }

    importPastes(historyWithPastes: CodeHistory): void {
        const undoStack: IndexedPaste[] = [];
        const redoStack: IndexedPaste[] = [];

        for (const [{ pastes }, operationIndex] of iterateUndoOperations(historyWithPastes)) {
            if (pastes) {
                for (const paste of pastes) {
                    undoStack.push({ operationIndex, paste });
                }
            }
        }

        for (const [{ pastes }, operationIndex] of iterateRedoOperations(historyWithPastes)) {
            if (pastes) {
                for (const paste of pastes) {
                    redoStack.push({ operationIndex, paste });
                }
            }
        }

        const historyAt: HistorySize = {
            undo: historyWithPastes.toUndo.length,
            redo: historyWithPastes.toRedo.length,
        };
        this.state = { undoStack, redoStack, historyAt };
    }

    export(): CodePasteTrackedState {
        return new CodePasteTrackedState(this.state);
    }
}

export class CodePasteTrackedState {
    constructor(private readonly state: CodePasteState) {}

    applyPastes(baseHistory: CodeHistory): CodeHistory {
        const undoPastes = groupPastesByOperationIndex(this.state.undoStack);
        const redoPastes = groupPastesByOperationIndex(this.state.redoStack);
        return {
            ...baseHistory,
            toUndo: Array.from(iterateUndoOperations(baseHistory), ([event, index]): CodeChangeEvent => {
                const pastes = undoPastes.get(index);
                return { ...event, pastes: pastes?.map((p) => p.paste) };
            }),
            toRedo: Array.from(iterateRedoOperations(baseHistory), ([event, index]): CodeChangeEvent => {
                const pastes = redoPastes.get(index);
                return { ...event, pastes: pastes?.map((p) => p.paste) };
            }),
        };
    }
}

type OperationWithIndex = [operation: CodeChangeEvent, operationIndex: number];

function* iterateUndoOperations(history: CodeHistory): Iterable<OperationWithIndex> {
    const { toUndo } = history;
    for (let i = 0; i < toUndo.length; i += 1) {
        const operation = toUndo[i];
        yield [operation, i];
    }
}

function* iterateRedoOperations(history: CodeHistory): Iterable<OperationWithIndex> {
    const { toUndo, toRedo } = history;
    for (let i = 0; i < toRedo.length; i += 1) {
        const operation = toRedo[i];
        // Compute the operation index for "redo" operation in such way that
        // it is follows the original operation order, i.e. reversed only for
        // "redo" operations to keep the same operation index when moving a paste
        // from undo stack to redo stack
        const operationIndex = toUndo.length + toRedo.length - i - 1;
        yield [operation, operationIndex];
    }
}

function groupPastesByOperationIndex(pasteStack: readonly IndexedPaste[]): Map<number, IndexedPaste[]> {
    const indexedPastes = new Map<number, IndexedPaste[]>();
    for (const paste of pasteStack) {
        let pastes = indexedPastes.get(paste.operationIndex);
        if (!pastes) {
            pastes = [];
            indexedPastes.set(paste.operationIndex, pastes);
        }
        pastes.push(paste);
    }
    return indexedPastes;
}

/**
 * Checks if a given pasted text is considered to be an "external" one
 * i.e. its content probably was copied from somewhere and not the code editor.
 */
export class ExternalPasteChecker {
    private _latestCopiedText = '';

    get latestCopiedText(): string {
        return this._latestCopiedText;
    }

    setLatestCopiedText(content: string): void {
        this._latestCopiedText = content;
    }

    isAnyInputLooksLikePaste(inserted: Text): boolean {
        // Consider any single multi-line input with >= 10 characters as a paste
        return inserted.lines >= 2 && inserted.length > 10;
    }

    check(inserted: Text, documentBefore: Text): CodePaste {
        const content = inserted.toString();
        const documentHasContent = documentBefore.toString().includes(content);
        const external = !(this.latestCopiedText === content || documentHasContent);
        return {
            contentLength: content.length,
            date: new Date().toISOString(),
            external,
        };
    }
}
